import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Autocomplete,
  useTheme
} from '@mui/material';
import {
  Search,
  QrCodeScanner,
  Save,
  Refresh,
  FileDownload,
  Category,
  Inventory,
  Edit,
  History,
  ArrowBack,
  FilterList,
  AddCircle,
  BrokenImage,
  RestaurantMenu
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import inventoryService from '../services/inventoryService';

const MotionDiv = motion.div;

const StockCount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  // States
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);

  // Adjustment dialog states
  const [physicalCount, setPhysicalCount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');

  // Quick adjustment states
  const [quickAdjustmentOpen, setQuickAdjustmentOpen] = useState(false);
  const [quickAdjustmentType, setQuickAdjustmentType] = useState('');
  const [selectedQuickItem, setSelectedQuickItem] = useState(null);

  // Adjustment history state
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch stock data from backend
  const fetchStockData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📦 StockCount: Fetching inventory data...');
      
      const response = await inventoryService.getStock();
      console.log('📦 StockCount: Response received:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setStockItems(response.data);
        console.log('✅ StockCount: Stock items set:', response.data.length, 'items');
      } else if (Array.isArray(response)) {
        setStockItems(response);
        console.log('✅ StockCount: Stock items set (direct array):', response.length, 'items');
      } else {
        console.warn('⚠️ StockCount: Unexpected response format:', response);
        setStockItems([]);
      }
      
    } catch (err) {
      console.error('❌ StockCount: Error fetching stock data:', err);
      setError(err.message || 'Failed to load stock data');
      setStockItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch adjustment history
  const fetchAdjustmentHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await inventoryService.getAdjustmentHistory();
      if (response && response.success) {
        setAdjustmentHistory(response.data || []);
      }
    } catch (err) {
      console.error('❌ Error fetching adjustment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch categories (from existing categories API)
  const fetchCategories = useCallback(async () => {
    try {
      console.log('📂 StockCount: Fetching categories...');
      
      const response = await inventoryService.getCategories();
      console.log('📂 StockCount: Categories response:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setCategories(response.data);
        console.log('✅ StockCount: Categories set:', response.data.length);
      } else {
        console.warn('⚠️ StockCount: Invalid categories response:', response);
        setCategories([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      setError('Failed to load categories');
      setCategories([]);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchStockData();
    fetchCategories();
  }, [fetchCategories]);

  // Load adjustment history when tab changes
  useEffect(() => {
    if (currentTab === 1) {
      fetchAdjustmentHistory();
    }
  }, [currentTab]);

  // Filtered items with safety check
  const filteredItems = Array.isArray(stockItems) 
    ? stockItems.filter(item => {
        if (!item || !item.name) return false;
        
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (item.barcode && item.barcode.includes(searchTerm));
        const matchesCategory = categoryFilter === 'all' || item.category_id === parseInt(categoryFilter);
        const matchesStatus = statusFilter === 'all' || 
                             (statusFilter === 'low' && (item.current_quantity || 0) <= (item.min_quantity || 0)) ||
                             (statusFilter === 'out' && (item.current_quantity || 0) === 0) ||
                             (statusFilter === 'normal' && (item.current_quantity || 0) > (item.min_quantity || 0));
        
        return matchesSearch && matchesCategory && matchesStatus;
      })
    : [];

  // Quick adjustment handlers with item selection
  const handleQuickDamage = () => {
    if (filteredItems.length === 0) {
      setError(t('inventory.noItemsAvailable') || 'No items available for adjustment');
      return;
    }
    
    setQuickAdjustmentType('damage');
    setSelectedQuickItem(null);
    setPhysicalCount('');
    setAdjustmentReason('damaged');
    setAdjustmentNotes(t('inventory.quickAdjustment.damaged') || 'Item Damaged');
    setQuickAdjustmentOpen(true);
  };

  const handleQuickRefund = () => {
    if (filteredItems.length === 0) {
      setError(t('inventory.noItemsAvailable') || 'No items available for adjustment');
      return;
    }
    
    setQuickAdjustmentType('refund');
    setSelectedQuickItem(null);
    setPhysicalCount('');
    setAdjustmentReason('customer_refund');
    setAdjustmentNotes(t('inventory.quickAdjustment.refund') || 'Customer Refund');
    setQuickAdjustmentOpen(true);
  };

  const handleQuickFound = () => {
    if (filteredItems.length === 0) {
      setError(t('inventory.noItemsAvailable') || 'No items available for adjustment');
      return;
    }
    
    setQuickAdjustmentType('found');
    setSelectedQuickItem(null);
    setPhysicalCount('');
    setAdjustmentReason('correction');
    setAdjustmentNotes(t('inventory.quickAdjustment.found') || 'Found Extra Item');
    setQuickAdjustmentOpen(true);
  };

  // Handle quick item selection
  const handleQuickItemChange = (event, newValue) => {
    setSelectedQuickItem(newValue);
    if (newValue) {
      const currentQty = newValue.current_quantity || 0;
      let newQty = currentQty;
      
      switch (quickAdjustmentType) {
        case 'damage':
        case 'refund':
          newQty = Math.max(0, currentQty - 1);
          break;
        case 'found':
          newQty = currentQty + 1;
          break;
        default:
          newQty = currentQty;
      }
      
      setPhysicalCount(newQty.toString());
    } else {
      setPhysicalCount('');
    }
  };

  // Handle stock adjustment
  const handleStockAdjustment = useCallback(async (itemId, physicalCount, reason, notes) => {
    try {
      const item = stockItems.find(i => i.item_id === itemId);
      if (!item) return;

      const adjustment = {
        itemId: itemId,
        physicalCount: physicalCount,
        reason: reason,
        notes: notes
      };

      if (bulkMode) {
        setPendingAdjustments(prev => [...prev.filter(p => p.itemId !== itemId), adjustment]);
      } else {
        await inventoryService.createStockAdjustment(adjustment);
        await fetchStockData(); // Refresh data
      }
    } catch (error) {
      console.error('Error adjusting stock:', error);
      setError('Failed to adjust stock: ' + error.message);
    }
  }, [stockItems, bulkMode]);

  // Handle bulk save
  const handleBulkSave = useCallback(async () => {
    if (pendingAdjustments.length === 0) return;
    
    try {
      for (const adjustment of pendingAdjustments) {
        await inventoryService.createStockAdjustment(adjustment);
      }
      setPendingAdjustments([]);
      await fetchStockData();
      setError(null);
    } catch (error) {
      console.error('Error saving bulk adjustments:', error);
      setError('Failed to save bulk adjustments: ' + error.message);
    }
  }, [pendingAdjustments]);

  // Get stock status
  const getStockStatus = (item) => {
    const currentQty = item.current_quantity || 0;
    const minQty = item.min_quantity || 0;
    
    if (currentQty === 0) return { label: 'Out of Stock', color: 'error' };
    if (currentQty <= minQty) return { label: 'Low Stock', color: 'warning' };
    return { label: 'Normal', color: 'success' };
  };

  // Handle adjustment dialog
  const handleOpenAdjustmentDialog = (item) => {
    setSelectedItem(item);
    setPhysicalCount(item.current_quantity?.toString() || '0');
    setAdjustmentReason('');
    setAdjustmentNotes('');
    setAdjustmentDialogOpen(true);
  };

  const handleCloseAdjustmentDialog = () => {
    setAdjustmentDialogOpen(false);
    setSelectedItem(null);
    setPhysicalCount('');
    setAdjustmentReason('');
    setAdjustmentNotes('');
  };

  const handleSaveAdjustment = async () => {
    if (!selectedItem || physicalCount === '' || !adjustmentReason) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await handleStockAdjustment(
        selectedItem.item_id,
        parseInt(physicalCount),
        adjustmentReason,
        adjustmentNotes
      );
      handleCloseAdjustmentDialog();
    } catch (error) {
      console.error('Error saving adjustment:', error);
    }
  };

  // Quick adjustment save
  const handleQuickSave = async () => {
    if (!selectedQuickItem || physicalCount === '' || !adjustmentReason) {
      setError('Please select an item and fill in all required fields');
      return;
    }

    try {
      await handleStockAdjustment(
        selectedQuickItem.item_id,
        parseInt(physicalCount),
        adjustmentReason,
        adjustmentNotes
      );
      setQuickAdjustmentOpen(false);
      setSelectedQuickItem(null);
    } catch (error) {
      console.error('Error saving quick adjustment:', error);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      console.log('📊 Exporting stock data...');
      const response = await inventoryService.exportStock();
      console.log('📊 Export response:', response);
      
      if (response && response.success) {
        // Convert to CSV and download
        const csvData = convertToCSV(response.data);
        downloadCSV(csvData, `stock-export-${new Date().toISOString().split('T')[0]}.csv`);
      }
    } catch (err) {
      console.error('❌ Export failed:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  // CSV conversion and download utilities
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Tab panel component
  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  // Adjustment reasons
  const adjustmentReasons = [
    { value: 'physical_count', label: t('inventory.reasons.physicalCount') || 'Physical Count' },
    { value: 'damaged', label: t('inventory.reasons.damaged') || 'Damaged Items' },
    { value: 'expired', label: t('inventory.reasons.expired') || 'Expired Items' },
    { value: 'theft', label: t('inventory.reasons.theft') || 'Theft/Loss' },
    { value: 'customer_refund', label: t('inventory.reasons.customerRefund') || 'Customer Refund' },
    { value: 'restock', label: t('inventory.reasons.restock') || 'New Stock Received' },
    { value: 'correction', label: t('inventory.reasons.correction') || 'System Correction' },
    { value: 'other', label: t('inventory.reasons.other') || 'Other' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title={t('common.back') || 'Back'}>
              <IconButton 
                onClick={() => navigate('/dashboard/inventory')}
                sx={{ 
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Inventory />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {t('inventory.stockCount.title') || 'Stock Count'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.stockCount.subtitle') || 'Track and adjust your inventory levels'}
              </Typography>
            </Box>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScanner />}
              onClick={() => {/* Implement barcode scanner */}}
            >
              {t('inventory.scanBarcode') || 'Scan Barcode'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchStockData}
              disabled={loading}
            >
              {t('common.refresh') || 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExport}
            >
              {t('common.export') || 'Export'}
            </Button>
          </Stack>
        </Box>
      </MotionDiv>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bulk Mode Controls */}
      {bulkMode && pendingAdjustments.length > 0 && (
        <MotionDiv
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleBulkSave}
                startIcon={<Save />}
              >
                {t('inventory.savePendingAdjustments', { count: pendingAdjustments.length }) || `Save ${pendingAdjustments.length} Adjustments`}
              </Button>
            }
          >
            {t('inventory.pendingAdjustments', { count: pendingAdjustments.length }) || `${pendingAdjustments.length} pending adjustments`}
          </Alert>
        </MotionDiv>
      )}

      {/* Filters */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FilterList />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('inventory.filtersAndSearch') || 'Filters & Search'}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkMode}
                    onChange={(e) => setBulkMode(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('inventory.bulkMode') || 'Bulk Mode'}
              />
            </Box>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                placeholder={t('inventory.searchPlaceholder') || 'Search items by name or barcode...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('inventory.category') || 'Category'}</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label={t('inventory.category') || 'Category'}
                >
                  <MenuItem value="all">{t('inventory.allCategories') || 'All Categories'}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={`category-${category.category_id}`} value={category.category_id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>{t('inventory.status') || 'Status'}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label={t('inventory.status') || 'Status'}
                >
                  <MenuItem value="all">{t('inventory.allStatus') || 'All Status'}</MenuItem>
                  <MenuItem value="normal">{t('inventory.status.normal') || 'Normal'}</MenuItem>
                  <MenuItem value="low">{t('inventory.status.low') || 'Low Stock'}</MenuItem>
                  <MenuItem value="out">{t('inventory.status.out') || 'Out of Stock'}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Card>
      </MotionDiv>

      {/* Main Content Tabs */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab 
                label={t('inventory.currentStock') || 'Current Stock'} 
                icon={<Inventory />}
                iconPosition="start"
              />
              <Tab 
                label={t('inventory.adjustmentHistory') || 'Adjustment History'} 
                icon={<History />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Current Stock Tab */}
          <TabPanel value={currentTab} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inventory.table.item') || 'Item'}</TableCell>
                      <TableCell>{t('inventory.table.category') || 'Category'}</TableCell>
                      <TableCell align="right">{t('inventory.table.currentStock') || 'Current Stock'}</TableCell>
                      <TableCell align="right">{t('inventory.table.minQuantity') || 'Min Quantity'}</TableCell>
                      <TableCell align="center">{t('inventory.table.status') || 'Status'}</TableCell>
                      <TableCell align="center">{t('inventory.table.actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((item, index) => {
                      const status = getStockStatus(item);
                      const hasPendingAdjustment = pendingAdjustments.some(p => p.itemId === item.item_id);
                      
                      return (
                        <TableRow 
                          key={`stock-item-${item.item_id}-${index}`}
                          sx={{ 
                            '&:hover': { bgcolor: 'action.hover' },
                            bgcolor: hasPendingAdjustment ? 'warning.lighter' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {item.image ? (
                                <Avatar 
                                  src={item.image} 
                                  alt={item.name}
                                  sx={{ width: 40, height: 40 }}
                                />
                              ) : (
                                <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.lighter' }}>
                                  {item.name?.charAt(0) || '?'}
                                </Avatar>
                              )}
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {item.name || t('inventory.unknownItem') || 'Unknown Item'}
                                </Typography>
                                {item.barcode && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.barcode}
                                  </Typography>
                                )}
                                {hasPendingAdjustment && (
                                  <Chip 
                                    label={t('inventory.pendingChange') || 'Pending'} 
                                    size="small" 
                                    color="warning"
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Chip 
                              label={item.category_name || 'Uncategorized'} 
                              size="small"
                              icon={<Category />}
                            />
                          </TableCell>
                          
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.current_quantity || 0} {item.unit_of_measure || 'pcs'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {item.min_quantity || 0} {item.unit_of_measure || 'pcs'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="center">
                            <Chip 
                              label={status.label}
                              color={status.color}
                              size="small"
                            />
                          </TableCell>
                          
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title={t('inventory.adjustStock') || 'Adjust Stock'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenAdjustmentDialog(item)}
                                  color={hasPendingAdjustment ? "warning" : "primary"}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title={t('inventory.quickScan') || 'Quick Scan'}>
                                <IconButton size="small" color="primary">
                                  <QrCodeScanner fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {filteredItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' 
                              ? (t('inventory.noItemsFound') || 'No items found matching your criteria')
                              : 'No stock items available'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Adjustment History Tab */}
          <TabPanel value={currentTab} index={1}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : adjustmentHistory.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inventory.table.item') || 'Item'}</TableCell>
                      <TableCell>{t('inventory.adjustmentReason') || 'Reason'}</TableCell>
                      <TableCell align="right">{t('inventory.variance') || 'Variance'}</TableCell>
                      <TableCell>{t('inventory.notes') || 'Notes'}</TableCell>
                      <TableCell>{t('common.adjustedBy') || 'Adjusted By'}</TableCell>
                      <TableCell>{t('common.date') || 'Date'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adjustmentHistory.map((adjustment) => (
                      <TableRow key={`adjustment-${adjustment.adjustment_id}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {adjustment.item_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={adjustmentReasons.find(r => r.value === adjustment.reason)?.label || adjustment.reason}
                            size="small"
                            color={adjustment.variance < 0 ? 'error' : adjustment.variance > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: adjustment.variance < 0 ? 'error.main' : adjustment.variance > 0 ? 'success.main' : 'text.primary'
                            }}
                          >
                            {adjustment.variance > 0 ? '+' : ''}{adjustment.variance}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {adjustment.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {adjustment.adjusted_by_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(adjustment.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('inventory.adjustmentHistoryPlaceholder') || 'No adjustment history found'}
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Card>
      </MotionDiv>

      {/* Quick Adjustment FABs */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          right: 24,
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          zIndex: 1050
        }}
      >
        <Tooltip title={t('inventory.quickAdjustment.damage') || 'Item Damaged'} placement="left">
          <Fab
            size="medium"
            color="error"
            onClick={handleQuickDamage}
            sx={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            aria-label={t('inventory.quickAdjustment.damage') || 'Item Damaged'}
          >
            <BrokenImage />
          </Fab>
        </Tooltip>
        
        <Tooltip title={t('inventory.quickAdjustment.refund') || 'Customer Refund'} placement="left">
          <Fab
            size="medium"
            color="warning"
            onClick={handleQuickRefund}
            sx={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(255, 152, 0, 0.3)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            aria-label={t('inventory.quickAdjustment.refund') || 'Customer Refund'}
          >
            <RestaurantMenu />
          </Fab>
        </Tooltip>
        
        <Tooltip title={t('inventory.quickAdjustment.found') || 'Found Extra'} placement="left">
          <Fab
            size="medium"
            color="success"
            onClick={handleQuickFound}
            sx={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.3)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            aria-label={t('inventory.quickAdjustment.found') || 'Found Extra'}
          >
            <AddCircle />
          </Fab>
        </Tooltip>
      </Box>

      {/* Stock Adjustment Dialog */}
      <Dialog
        open={adjustmentDialogOpen}
        onClose={handleCloseAdjustmentDialog}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus={true}
        disableAutoFocus={false}
        disableEnforceFocus={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }
        }}
        aria-labelledby="adjustment-dialog-title"
        aria-describedby="adjustment-dialog-description"
      >
        <DialogTitle id="adjustment-dialog-title">
          {t('inventory.adjustStock') || 'Adjust Stock'} - {selectedItem?.name}
        </DialogTitle>
        <DialogContent id="adjustment-dialog-description">
          {selectedItem && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('inventory.currentInformation') || 'Current Information'}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
                <Typography variant="body2">
                  {t('inventory.currentStock') || 'Current Stock'}: {selectedItem.current_quantity || 0} {selectedItem.unit_of_measure || 'pcs'}
                </Typography>
                <Typography variant="body2">
                  {t('inventory.minQuantity') || 'Min Quantity'}: {selectedItem.min_quantity || 0} {selectedItem.unit_of_measure || 'pcs'}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t('inventory.physicalCount') || 'Physical Count'}
                type="number"
                value={physicalCount}
                onChange={(e) => setPhysicalCount(e.target.value)}
                margin="normal"
                required
                inputProps={{ min: 0 }}
              />

              {physicalCount !== '' && (
                <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, my: 2 }}>
                  <Typography variant="body2">
                    {t('inventory.variance') || 'Variance'}: {
                      parseInt(physicalCount) - (selectedItem.current_quantity || 0)
                    } {selectedItem.unit_of_measure || 'pcs'}
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth margin="normal" required>
                <InputLabel>{t('inventory.adjustmentReason') || 'Adjustment Reason'}</InputLabel>
                <Select
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  label={t('inventory.adjustmentReason') || 'Adjustment Reason'}
                >
                  {adjustmentReasons.map((reason) => (
                    <MenuItem key={`reason-${reason.value}`} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('inventory.notes') || 'Notes (Optional)'}
                multiline
                rows={3}
                value={adjustmentNotes}
                onChange={(e) => setAdjustmentNotes(e.target.value)}
                margin="normal"
                placeholder={t('inventory.notesPlaceholder') || 'Additional notes about this adjustment...'}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAdjustmentDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAdjustment}
            disabled={!physicalCount || !adjustmentReason}
          >
            {t('inventory.saveAdjustment') || 'Save Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Adjustment Dialog */}
      <Dialog
        open={quickAdjustmentOpen}
        onClose={() => setQuickAdjustmentOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus={true}
        disableAutoFocus={false}
        disableEnforceFocus={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }
        }}
        aria-labelledby="quick-adjustment-dialog-title"
        aria-describedby="quick-adjustment-dialog-description"
      >
        <DialogTitle id="quick-adjustment-dialog-title">
          {/* ✅ FIXED: Removed duplicate "Quick adjustment: " prefix */}
          {quickAdjustmentType === 'damage' ? (t('inventory.quickAdjustment.damage') || 'Item Damaged') :
           quickAdjustmentType === 'refund' ? (t('inventory.quickAdjustment.refund') || 'Customer Refund') :
           quickAdjustmentType === 'found' ? (t('inventory.quickAdjustment.found') || 'Found Extra Item') : ''
          }
        </DialogTitle>
        <DialogContent id="quick-adjustment-dialog-description">
          <Box sx={{ pt: 2 }}>
            {/* Item Selection */}
            <Autocomplete
              value={selectedQuickItem}
              onChange={handleQuickItemChange}
              options={filteredItems}
              getOptionLabel={(option) => option.name || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('inventory.selectItem') || 'Select Item'}
                  placeholder={t('inventory.searchItems') || 'Search items...'}
                  required
                />
              )}
              // renderOption={(props, option) => {
              //    // ✅ FIXED: Properly extract key from props and create unique key
              //   const { key, ...otherProps } = props;
              //   const uniqueKey = `quick-item-${option.item_id}-${Date.now()}-${Math.random()}`;
                
              //   return (
              //         <Box 
              //           component="li" 
              //           key={uniqueKey}
              //           {...otherProps} 
              //           sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
              //         >
              //           {option.image ? (
              //             <Avatar src={option.image} sx={{ width: 32, height: 32 }} />
              //           ) : (
              //             <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter' }}>
              //               {option.name?.charAt(0) || '?'}
              //             </Avatar>
              //           )}
              //           <Box>
              //             <Typography variant="body2" sx={{ fontWeight: 600 }}>
              //               {option.name}
              //             </Typography>
              //             <Typography variant="caption" color="text.secondary">
              //               Current: {option.current_quantity || 0} {option.unit_of_measure || 'pcs'} | 
              //               Category: {option.category_name}
              //             </Typography>
              //           </Box>
              renderOption={(props, option, { index }) => {
                // ✅ FIXED: Properly extract and remove key from props
                const { key: _, ...otherProps } = props;
                const uniqueKey = `quick-item-${option.item_id}-${index}-${option.name}-${option.category_id || 'no-cat'}`;

                return (
                  <Box 
                    component="li" 
                    key={uniqueKey}
                    {...otherProps} 
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    {option.image ? (
                      <Avatar src={option.image} sx={{ width: 32, height: 32 }} />
                    ) : (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter' }}>
                        {option.name?.charAt(0) || '?'}
                      </Avatar>
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Current: {option.current_quantity || 0} {option.unit_of_measure || 'pcs'} | 
                        Category: {option.category_name}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              sx={{ mb: 2 }}
            />

            {selectedQuickItem && (
              <>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedQuickItem.name}
                  </Typography>
                  <Typography variant="body2">
                    {t('inventory.currentStock') || 'Current'}: {selectedQuickItem.current_quantity || 0} {selectedQuickItem.unit_of_measure || 'pcs'}
                  </Typography>
                  <Typography variant="body2">
                    {t('inventory.category') || 'Category'}: {selectedQuickItem.category_name}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label={t('inventory.physicalCount') || 'New Quantity'}
                  type="number"
                  value={physicalCount}
                  onChange={(e) => setPhysicalCount(e.target.value)}
                  margin="normal"
                  required
                  inputProps={{ min: 0 }}
                />

                {physicalCount !== '' && (
                  <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, my: 2 }}>
                    <Typography variant="body2">
                      {t('inventory.variance') || 'Variance'}: {
                        parseInt(physicalCount) - (selectedQuickItem.current_quantity || 0)
                      } {selectedQuickItem.unit_of_measure || 'pcs'}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, my: 2 }}>
                  <Typography variant="body2">
                    {t('inventory.adjustmentReason') || 'Reason'}: {adjustmentReasons.find(r => r.value === adjustmentReason)?.label}
                  </Typography>
                  {adjustmentNotes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('inventory.notes') || 'Notes'}: {adjustmentNotes}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setQuickAdjustmentOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleQuickSave}
            disabled={!selectedQuickItem || physicalCount === '' || !adjustmentReason}
          >
            {t('inventory.confirm') || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockCount;